import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import GoogleAuthButton from '../components/GoogleAuthButton';
import {
  CheckIcon,
  ScaleIcon,
  LockIcon,
  AlertCircleIcon,
  BuildingIcon
} from '../components/Icons';
import './Auth.css';
import { authAPI } from '../services/api';

const SignupNew: React.FC = () => {
  React.useEffect(() => {
    // Ensure page starts at the top (preview page may leave scroll offset)
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionState, setCompletionState] = useState('');
  const [completionCity, setCompletionCity] = useState('');
  const [completionAgreed, setCompletionAgreed] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [completionLoading, setCompletionLoading] = useState(false);
  const { signup, googleLogin, updateProfile, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const { getLocalizedPath, currentLanguage } = useLanguage();

  const brazilStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError(t('auth:signup.errors.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth:signup.errors.passwordTooShort'));
      return;
    }

    if (!agreedToTerms) {
      setError(t('auth:signup.errors.termsRequired'));
      return;
    }

    if (!state) {
      setError(t('auth:signup.errors.stateRequired'));
      return;
    }

    if (!city.trim()) {
      setError(t('auth:signup.errors.cityRequired'));
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, fullName, state, city.trim(), currentLanguage);
      // Redirect to confirmation pending page instead of upload page
      navigate(getLocalizedPath('/confirmation-pending'), { state: { email } });
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('auth:signup.errors.generic');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true);
    setError('');

    try {
      await googleLogin(accessToken);
      // Fetch the freshest user profile to decide if we need completion
      let profile = user;
      try {
        const res = await authAPI.me();
        profile = res.data;
      } catch {
        profile = user;
      }

      const existingState = (profile?.state || '').trim();
      const existingCity = (profile?.city || '').trim();

      if (existingState && existingCity) {
        navigate(getLocalizedPath('/upload'));
        return;
      }

      // Prefill completion modal with any available data
      setCompletionState(existingState || state);
      setCompletionCity(existingCity || city);
      setCompletionAgreed(false);
      setShowCompletionModal(true);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('auth:signup.errors.googleError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('auth:signup.errors.googleError'));
  };

  return (
    <div className="auth-page auth-page--refined auth-page--signup">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-content">
          <div className="auth-logo" onClick={() => navigate(getLocalizedPath('/'))}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="auth-header-actions">
            <button onClick={() => navigate(getLocalizedPath('/'))} className="btn-text">
              {t('auth:login.backToHome')}
            </button>
            <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-primary-auth">
              {t('auth:signup.signIn')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="auth-main">
        <div className="auth-container">
          <div className="auth-grid">
            {/* Left Column - Form */}
            <div className="auth-form-column">
              <div className="auth-card">
                <div className="auth-card-header">
                  <h1>{t('auth:signup.title')}</h1>
                  <p>{t('auth:signup.subtitle')}</p>
                </div>

                {error && (
                  <div className="auth-error">
                    <AlertCircleIcon size={20} />
                    <span>{error}</span>
                  </div>
                )}

                <GoogleAuthButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text={t('auth:signup.signUpWithGoogle')}
                  disabled={loading}
                />
                <div className="auth-google-hint">{t('auth:signup.googleHint')}</div>

                <div className="auth-divider">
                  <span>{t('common:or')}</span>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-field">
                    <label htmlFor="email">{t('auth:signup.email')}</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth:signup.emailPlaceholder')}
                      required
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="password">{t('auth:signup.password')}</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth:signup.passwordPlaceholder')}
                      required
                      minLength={8}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="confirmPassword">{t('auth:signup.confirmPassword')}</label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('auth:signup.confirmPasswordPlaceholder')}
                      required
                      minLength={8}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="fullName">{t('auth:signup.fullName')}</label>
                    <input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={t('auth:signup.fullNameOptional')}
                      disabled={loading}
                      autoComplete="name"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="state">{t('auth:signup.state')}</label>
                    <select
                      id="state"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      disabled={loading}
                      required
                    >
                      <option value="">{t('auth:signup.statePlaceholder')}</option>
                      {brazilStates.map((uf) => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label htmlFor="city">{t('auth:signup.city')}</label>
                    <input
                      id="city"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t('auth:signup.cityPlaceholder')}
                      disabled={loading}
                      required
                      autoComplete="address-level2"
                    />
                  </div>

                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      disabled={loading}
                    />
                    <label htmlFor="terms">
                      {t('auth:signup.termsAgree')}{' '}
                      <a href={getLocalizedPath('/termos-de-uso')} className="link-inline">{t('auth:signup.termsLink')}</a>,{' '}
                      <a href={getLocalizedPath('/politica-de-privacidade')} className="link-inline">{t('auth:signup.privacyLink')}</a>,{' '}
                      <a href={getLocalizedPath('/politica-de-cookies')} className="link-inline">{t('auth:signup.cookiesLink')}</a> {t('auth:signup.and')}{' '}
                      <a href={getLocalizedPath('/aviso-legal')} className="link-inline">{t('auth:signup.legalLink')}</a>.
                    </label>
                  </div>

                  <button type="submit" className="btn-primary-full" disabled={loading || !agreedToTerms}>
                    {loading ? t('auth:signup.submitting') : t('auth:signup.submit')}
                  </button>
                  <div className="auth-cta-hint">{t('auth:signup.ctaHint')}</div>

                </form>

                <div className="auth-trust-footer">
                  <LockIcon size={16} />
                  <span>{t('auth:signup.secureNote')}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Presentation */}
            <div className="auth-presentation-column">
              <div className="auth-presentation">
                <h2>{t('auth:signup.presentationTitle')}</h2>

                <div className="presentation-benefits">
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <CheckIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('auth:signup.benefit1Title')}</h3>
                      <p>{t('auth:signup.benefit1Desc')}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <AlertCircleIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('auth:signup.benefit2Title')}</h3>
                      <p>{t('auth:signup.benefit2Desc')}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <ScaleIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('auth:signup.benefit3Title')}</h3>
                      <p>{t('auth:signup.benefit3Desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="presentation-trust">
                  <div className="trust-badge">
                    <LockIcon size={16} />
                    <span>{t('common:trustBadges.lgpd')}</span>
                  </div>
                  <div className="trust-badge">
                    <BuildingIcon size={16} />
                    <span>{t('common:trustBadges.encrypted')}</span>
                  </div>
                  <div className="trust-badge">
                    <span>🇧🇷</span>
                    <span>{t('common:trustBadges.madeForBrazil')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showCompletionModal && (
        <div className="auth-modal-backdrop">
          <div className="auth-modal">
            <h2>{t('auth:signup.completeProfile', { defaultValue: 'Complete seu cadastro' })}</h2>
            <p className="auth-modal-sub">
              {t('auth:signup.completeProfileHint', { defaultValue: 'Informe seu estado, cidade e aceite os termos para continuar.' })}
            </p>

            {completionError && (
              <div className="auth-error" style={{ marginTop: 8 }}>
                <AlertCircleIcon size={20} />
                <span>{completionError}</span>
              </div>
            )}

            <div className="form-field" style={{ marginTop: 12 }}>
              <label htmlFor="completion-state">{t('auth:signup.state')}</label>
              <select
                id="completion-state"
                value={completionState}
                onChange={(e) => setCompletionState(e.target.value)}
                disabled={completionLoading}
              >
                <option value="">{t('auth:signup.statePlaceholder')}</option>
                {brazilStates.map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="completion-city">{t('auth:signup.city')}</label>
              <input
                id="completion-city"
                type="text"
                value={completionCity}
                onChange={(e) => setCompletionCity(e.target.value)}
                placeholder={t('auth:signup.cityPlaceholder')}
                disabled={completionLoading}
                autoComplete="address-level2"
              />
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="completion-terms"
                checked={completionAgreed}
                onChange={(e) => setCompletionAgreed(e.target.checked)}
                disabled={completionLoading}
              />
              <label htmlFor="completion-terms">
                {t('auth:signup.termsAgree')}{' '}
                <a href={getLocalizedPath('/termos-de-uso')} className="link-inline">{t('auth:signup.termsLink')}</a>,{' '}
                <a href={getLocalizedPath('/politica-de-privacidade')} className="link-inline">{t('auth:signup.privacyLink')}</a>,{' '}
                <a href={getLocalizedPath('/politica-de-cookies')} className="link-inline">{t('auth:signup.cookiesLink')}</a> {t('auth:signup.and')}{' '}
                <a href={getLocalizedPath('/aviso-legal')} className="link-inline">{t('auth:signup.legalLink')}</a>.
              </label>
            </div>

            <div className="auth-modal-actions">
              <button
                className="btn-primary-full"
                type="button"
                disabled={completionLoading}
                onClick={async () => {
                  setCompletionError('');
                  if (!completionState) {
                    setCompletionError(t('auth:signup.errors.stateRequired'));
                    return;
                  }
                  if (!completionCity.trim()) {
                    setCompletionError(t('auth:signup.errors.cityRequired'));
                    return;
                  }
                  if (!completionAgreed) {
                    setCompletionError(t('auth:signup.errors.termsRequired'));
                    return;
                  }
                  setCompletionLoading(true);
                  try {
                    await updateProfile({
                      state: completionState,
                      city: completionCity.trim(),
                    });
                    setShowCompletionModal(false);
                    navigate(getLocalizedPath('/upload'));
                  } catch (err: any) {
                    const errorDetail = err?.response?.data?.detail;
                    const msg = typeof errorDetail === 'string'
                      ? errorDetail
                      : errorDetail?.message || t('auth:signup.errors.generic');
                    setCompletionError(msg);
                  } finally {
                    setCompletionLoading(false);
                  }
                }}
              >
                {completionLoading ? t('auth:signup.submitting') : t('auth:signup.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="auth-footer-section">
        <div className="auth-footer-content">
          <div className="footer-left">
            <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo-small" />
            <div className="footer-copy">
              <p className="footer-brand">ContratoSeguro</p>
              <p className="footer-tagline">{t('common:footer.tagline')}</p>
            </div>
          </div>
          <div className="footer-center">
            <a href={getLocalizedPath('/termos-de-uso')}>{t('common:footer.termsOfUse')}</a>
            <a href={getLocalizedPath('/politica-de-privacidade')}>{t('common:footer.privacyPolicy')}</a>
            <a href={getLocalizedPath('/politica-de-cookies')}>{t('common:footer.cookiesPolicy')}</a>
            <a href={getLocalizedPath('/aviso-legal')}>{t('common:footer.legalNotice')}</a>
          </div>
          <div className="footer-right">
            <p className="footer-heading">{t('common:footer.contact')}</p>
            <p className="footer-contact">suporte@contratoseguro.com.br</p>
            <p className="footer-heading">{t('common:footer.lgpd')}</p>
            <p className="footer-contact">privacidade@contratoseguro.com.br</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignupNew;
